import { useCallback, useEffect, useRef, useState } from 'react';
import { formatPosition, useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import { ApiError, enterQueue, leaveQueue, livekitConfigured, subscribeQueue } from '@/api';
import { useGeoEvidence } from '@/media/useGeoEvidence';
import { requestDroomReschedule } from '@/api/booking';
import type { StepId } from '@/types';
import { queueRefusalStep } from '../queueRefusal';
import { type ArrivalRefusal, parseArrivalRefusal } from '../arrivalWindow';
import { createAdvanceOnce, createLeaveBeaconGuard } from './waitingRoomPresence';
import { MissedWindow, ScheduledArrival } from './ArrivalWindowScreens';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { Stepper } from '../components/Stepper';
import { SecurePill } from '../components/SecurePill';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';

/**
 * SCREEN 04 · WAITING ROOM — a real, backend-driven queue. Position + ETA are
 * server values (via subscribeQueue), NOT the as-built hardcoded "position 2"
 * with a cosmetic counter (DATAROOM_AUDIT §1). Shows a skeleton while pending
 * and never invents a number. "Your spot is saved" sets up drop-safe resume.
 *
 * CD32: a customer with a confirmed booking only reaches the queue INSIDE their
 * arrival window. Outside it the be answers a typed refusal and this screen hands
 * the whole stage to ArrivalWindowScreens — the customer's appointment, not an
 * error. Walk-ins and older backends never take that branch (arrivalWindow.ts).
 */
export function WaitingRoom({ tenant }: StepProps): JSX.Element {
  const { t, locale } = useT();
  const sessionId = useSessionStore((s) => s.session.sessionId) as string;
  const queue = useSessionStore((s) => s.queue);
  const setQueue = useSessionStore((s) => s.setQueue);
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);

  // AC13's JOIN-moment capture (KYCID·00001-00006-00002). Before this, the customer's position and
  // — more importantly — their SOURCE IP were recorded only on the call screen, so a customer who
  // never got that far left no geo evidence at all and GEO_IP never reported. The IP, its ASN,
  // country and VPN signal are derived server-side from this very request, so this posts even when
  // the fix is denied or times out: that post IS how the network evidence lands.
  //
  // 'background' — it fires once and never blocks. Holding someone in the queue behind a location
  // prompt would be worse than asking on the call screen, where the gate still runs and appends a
  // second attempt to the same ladder.
  useGeoEvidence(sessionId, livekitConfigured(), 'background');
  const goToStep = useSessionStore((s) => s.goToStep);

  // "Reschedule for later" was a dead button — no onClick at all, on the highest-anxiety screen.
  // It now stamps the reschedule request on the booking (best-effort; the re-pick is the real act)
  // and returns the customer to the schedule step to pick a new slot. Their spot in TODAY's queue
  // is what they are giving up, and the copy above the button already says the spot is saved, so
  // the state clears honestly rather than pretending both can be true.
  const [rescheduling, setRescheduling] = useState(false);
  const reschedule = () => {
    if (rescheduling) return;
    setRescheduling(true);
    void requestDroomReschedule(sessionId, 'customer-rescheduled-from-waiting-room')
      .catch(() => {})
      .finally(() => {
        goToStep('schedule');
      });
  };

  // Guarantee the session is in the warm band. 'waiting' is always FRAME_TAIL — the convergence point after
  // every PRE_CALL check — so this covers a capture-less DigiLocker journey (which never called queue:enter)
  // as well as the self-capture path (DocumentCapture already enqueued → this is an idempotent echo).
  //
  // Item 8b (droom half): a 422 here is the be NAMING exactly what is still owed
  // (consent / artefacts / a pre-call check). It used to be swallowed
  // (`void enterQueue().catch(() => {})`) and the customer sat on the eternal
  // "Finding your place in line…" skeleton. The refusal is now rendered, with a
  // route back to the step that can fix it. Non-422 failures stay best-effort:
  // the subscribeQueue poll is the source of truth for a transient wobble.
  //
  // CD32 (owner screenshot, 2026-09-01 11:34): with a confirmed booking the be now answers a TYPED
  // arrival-window problem instead of admitting the customer — a 12:30 appointment opened at 11:34
  // used to walk straight into the live queue at "#2 in line". Those two codes are SCREENS, not
  // error states, so they are read FIRST and take over the whole stage. Every other refusal, and
  // every older be that has no typed codes at all, falls through to exactly the behaviour above.
  const flowOrder = useSessionStore((s) => s.flowOrder);
  const [refusal, setRefusal] = useState<{ detail: string; target: StepId | null } | null>(
    null,
  );
  const [arrival, setArrival] = useState<ArrivalRefusal | null>(null);
  const attemptEnterQueue = useCallback(() => {
    setRefusal(null);
    enterQueue(sessionId)
      .then(() => {
        setRefusal(null);
        setArrival(null); // admitted — the arrival gate is behind us
      })
      .catch((e: unknown) => {
        const arrivalRefusal = parseArrivalRefusal(e);
        if (arrivalRefusal) {
          setArrival(arrivalRefusal);
          return;
        }
        setArrival(null);
        if (e instanceof ApiError && e.status === 422) {
          setRefusal({
            detail: e.message, // the contract: the 422 message names what's missing
            target: queueRefusalStep(e.message, flowOrder),
          });
        }
        /* other failures: keep the skeleton; the poll below keeps trying */
      });
  }, [sessionId, flowOrder]);
  useEffect(() => {
    attemptEnterQueue();
  }, [attemptEnterQueue]);

  // Subscribe to live queue updates for this session. The poll tick doubles as the customer's
  // presence heartbeat (#4) — POST /queue:heartbeat, falling back to GET on an older be — so the
  // officer console can tell a customer who is genuinely waiting from one who closed the tab.
  //
  // NOT while the arrival gate holds: a customer the be refused is not in the queue, so
  // heartbeating presence for them would report a waiting customer who is not waiting — and would
  // poll for the hour before their slot. The subscription starts the moment entry succeeds.
  const gated = arrival !== null;
  useEffect(() => {
    if (gated) return undefined;
    const unsub = subscribeQueue(sessionId, setQueue);
    return unsub;
  }, [gated, sessionId, setQueue]);

  // An OFFICER IS ON THE CALL → warm-connect into the live leg.
  //
  // This used to fire on `source === 'ready'` when 'ready' merely meant the session had finished
  // warming, so the customer was released into an empty room and waited THERE — staring at a blank
  // call screen — instead of in the queue where the position and reassurance live. Worse, the
  // recorder arms on the LIVE transition, so the customer could be in a regulated room before any
  // recorder was attached. Both halves are now required: the server says ready AND names an officer.
  // The backend refuses a customer join token in the same circumstances, so a client that got this
  // wrong would be refused rather than admitted.
  const officerOnCall = queue.source === 'ready' && queue.agentPresent;
  const officerOnCallRef = useRef(officerOnCall);
  officerOnCallRef.current = officerOnCall;

  // #4 → testmap #12: the leave beacon, DEBOUNCED. The old handler beaconed queue:leave on every
  // visibility:hidden and every pagehide — iOS flaps both on mere backgrounding (notification
  // shade, permission sheets, app-switcher peeks, bfcache pagehide with persisted=true), so a
  // customer who was actively heartbeating read as gone and the 20-Aug HDFC claim window was
  // poisoned. Policy lives in waitingRoomPresence.ts (pure, pinned by tests): only a page that
  // STAYS hidden (15s, inside the be's 45s staleness) or a REAL teardown (pagehide persisted=false)
  // sends a leave — and never while an officer is on the call (the handoff window: the customer is
  // transitioning to the call screen, not abandoning the queue). Still PRESENCE only — never a
  // session-closed signal (resume stays possible; the be reaper owns terminality).
  useEffect(() => {
    if (gated) return undefined; // never beacon a leave for a customer who was never let in
    const guard = createLeaveBeaconGuard({
      send: () => leaveQueue(sessionId),
      officerOnCall: () => officerOnCallRef.current,
    });
    const onPageHide = (e: PageTransitionEvent) => guard.onPageHide(e.persisted === true);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') guard.onHidden();
      else guard.onVisible();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      guard.dispose();
    };
  }, [gated, sessionId]);

  // Testmap #12, the transition half: advance is LATCHED (waiting→call exactly once) and carries a
  // visibility flush — the 900ms confirmation beat rides a setTimeout iOS freezes while the tab is
  // backgrounded, so a customer who returns after the claim landed advances the instant the page is
  // visible again instead of whenever the thawed timer fires (the 20-Aug customer waited 3m48s next
  // to a claimed call and gave up 9 seconds before the officer joined the room).
  const advanceOnceRef = useRef<(() => void) | null>(null);
  if (advanceOnceRef.current === null) advanceOnceRef.current = createAdvanceOnce(advance);
  useEffect(() => {
    if (!officerOnCall) return undefined;
    const go = advanceOnceRef.current!;
    const t = setTimeout(go, 900);
    const onVisible = () => {
      if (document.visibilityState === 'visible') go();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [officerOnCall]);

  // CD32: the arrival gate replaces the waiting-room STAGE and nothing else. No completed step is
  // touched, no state is reset, and the reschedule affordance handed down is this screen's own —
  // one entry point for the act, wherever the customer taps it.
  if (arrival !== null && !officerOnCall) {
    return arrival.kind === 'before' ? (
      <ScheduledArrival
        tenant={tenant}
        refusal={arrival}
        onWindowOpen={attemptEnterQueue}
        onReschedule={reschedule}
        rescheduling={rescheduling}
      />
    ) : (
      <MissedWindow
        tenant={tenant}
        refusal={arrival}
        onReschedule={reschedule}
        rescheduling={rescheduling}
      />
    );
  }

  const pending = queue.source === 'pending';
  const position = queue.position;
  const total = queue.total ?? 1;
  const pct =
    position != null && total > 0
      ? Math.min(100, Math.max(8, Math.round(((total - position) / total) * 100)))
      : 8;
  const etaMin =
    queue.etaSeconds != null ? Math.max(1, Math.round(queue.etaSeconds / 60)) : null;

  return (
    <div className="app-view">
      <AppBar
        tenant={tenant}
        subtitle={t('droom.wait.subtitle')}
        right={<SecurePill kind="queue" label={t('droom.pill.queue')} />}
      />
      <Stepper current="waiting" />

      <div className="wait-hero">
        <span className="wait-ring r1" />
        <span className="wait-ring r2" />
        <span className="wait-ring r3" />
        <span className="wait-core">
          <Icon name="users" size={38} />
        </span>
      </div>

      {refusal && !officerOnCall ? (
        /* Item 8b: the be's honest queue refusal — never the eternal skeleton.
           The 422 message names what is missing; the route goes back to the
           step that can complete it (when the wording maps to one). */
        <section className="card stack" style={{ padding: 16, marginTop: 16 }} role="alert">
          <h2>{t('droom.wait.refusal.title')}</h2>
          <p className="sub">{t('droom.wait.refusal.body')}</p>
          <p className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {refusal.detail}
          </p>
          {refusal.target ? (
            <Button onClick={() => goToStep(refusal.target as StepId)}>
              {t('droom.wait.refusal.goback')}
            </Button>
          ) : null}
          <Button variant={refusal.target ? 'ghost' : 'primary'} onClick={attemptEnterQueue}>
            {t('droom.action.tryAgain')}
          </Button>
        </section>
      ) : (
        <>
          <div className="queue-num">
            <div className="n mono">
              <span className="hash">#</span>
              {pending ? <span className="pending">—</span> : (position ?? 0)}
            </div>
            <div className="l">
              {officerOnCall
                ? t('droom.wait.officerJoined')
                : pending || position == null
                  ? t('droom.wait.finding')
                  : position === 1
                    ? t('droom.wait.next')
                    : t('droom.wait.position', { position: formatPosition(locale, position) })}
            </div>
            {!pending && etaMin != null ? (
              <div className="eta mono">
                <Icon name="clock" size={14} />{t('droom.wait.eta', { min: String(etaMin) })}
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: 20 }}>
            <div
              className="wait-bar"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('droom.wait.progressAria')}
            >
              <i style={{ width: `${pct}%` }} />
            </div>
          </div>
        </>
      )}

      <div className="spacer" />

      <div className="reassure-row" style={{ marginTop: 16 }}>
        <div className="reassure">
          <div className="ic">
            <Icon name="monitor" size={17} />
          </div>
          {t('droom.wait.keepOpen')}
        </div>
        <div className="reassure">
          <div className="ic">
            <Icon name="autoconnect" size={17} />
          </div>
          {t('droom.wait.autoconnect')}
        </div>
        <div className="reassure">
          <div className="ic">
            <Icon name="shield" size={17} />
          </div>
          {t('droom.wait.spotSaved')}
        </div>
      </div>

      <div className="stack-sm" style={{ marginTop: 14 }}>
        <Button variant="soft" onClick={reschedule} disabled={rescheduling} type="button">
          {rescheduling ? t('droom.wait.rescheduling') : t('droom.wait.reschedule')}
        </Button>
      </div>
    </div>
  );
}
